import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { shareReplay, filter, map } from 'rxjs/operators';
import ENV from '@/constants/ENV';
    
export type EventPayload = {
    type: string;
    data: any;
};

export type DisconnectEvent = {
    code: number;
    reason: string;
    wasClean: boolean;
    timestamp: number;
    reconnectAttempt?: number;
};

interface SocketOptions {
    baseUrl: string;
    token?: string;
    maxReconnectAttempts?: number;
    reconnectDelay?: number;
    debug?: boolean;
}

class SocketService {
    private socket: WebSocket | null = null;

    private connected$ = new BehaviorSubject<boolean>(false);
    private error$ = new Subject<any>();
    private _events$ = new Subject<EventPayload>();
    private _destroy$ = new Subject<void>();
    private disconnect$ = new Subject<DisconnectEvent>();

    private projectId: string | null = null;
    private sessionId: string | null = null;
    private token: string | null = null;
    private baseUrl: string = '';

    // Reconnection tracking
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 500;
    private reconnectTimer: any = null;
    private manualDisconnect = false;
    private debug = ENV.ENV_MODE === 'development' ? true : false;

    //ENV is dev  make debug true

    init(options: SocketOptions): void {
        this.baseUrl = options.baseUrl;
        this.token = options.token || null;
        this.maxReconnectAttempts = options.maxReconnectAttempts || 5;
        this.reconnectDelay = options.reconnectDelay || 500;
        this.debug = options.debug || false;

        // Clear any existing reconnect timer
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        // Reset reconnect attempts on manual connection
        this.reconnectAttempts = 0;
        this.manualDisconnect = false;

        // If there's an existing socket, close it properly
        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }

        this.connect();
    }

    private connect(): void {
        let url = this.baseUrl;

        if (this.token) {
            url = `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(this.token)}`;
        }

        if (this.debug) {
            console.log('[SocketService] Connecting to WebSocket with URL:', url);
        }

        this.socket = new WebSocket(url);

        this.socket.addEventListener('open', () => {
            if (this.debug) {
                console.log('[SocketService] WebSocket connected');
            }


            this.connected$.next(true);
            // Reset reconnect attempts on successful connection
            this.reconnectAttempts = 0;
        });

        this.socket.addEventListener('close', (event) => {
            if (this.debug) {
                console.log('[SocketService] WebSocket disconnected:', event);
            }


            this.connected$.next(false);

            // Emit disconnect event with detailed information
            const disconnectEvent: DisconnectEvent = {
                code: event.code,
                reason: event.reason || this.getDisconnectReason(event.code),
                wasClean: event.wasClean,
                timestamp: Date.now(),
                reconnectAttempt: this.reconnectAttempts
            };

            this.disconnect$.next(disconnectEvent);

            // Emit as regular event too for backwards compatibility
            this._events$.next({
                type: 'disconnect',
                data: disconnectEvent
            });

            // Attempt to reconnect if not a manual close
            if (!this.manualDisconnect) {
                this.attemptReconnect();
            }
        });

        this.socket.addEventListener('error', (error) => {
            if (this.debug) {
                console.error('[SocketService] WebSocket connection error:', error);
            }
            this.error$.next(error);
        });

        this.socket.addEventListener('message', (event) => {
            try {
                const parsedData = JSON.parse(event?.data);


                // Handle session info
                if (parsedData.projectId) this.projectId = parsedData.projectId;
                if (parsedData.sessionId) this.sessionId = parsedData.sessionId;

                // Determine message type
                let eventType = 'message';

                // Check for event types in different fields
                if (parsedData.event_type) {
                    eventType = parsedData.event_type;
                } else if (parsedData.event) {
                    eventType = parsedData.event;
                }

                if (this.debug) {
                    console.log(`[SocketService] Received event type: ${eventType}`, parsedData);
                }

                // If it's an error event, also emit a generic 'socket.error' event
                if (eventType.endsWith('.error')) {
                    this._events$.next({ type: 'socket.error', data: parsedData });
                }

                // Emit the typed event
                this._events$.next({ type: eventType, data: parsedData });
            } catch (err) {
                // If not JSON, emit as raw message
                if (this.debug) {
                    console.log('[SocketService] Received non-JSON message');
                }
                console.error(err);
                this._events$.next({ type: 'message', data: event.data });
            }
        });
    }

    // Helper method to get human-readable disconnect reasons
    private getDisconnectReason(code: number): string {
        const reasons: { [key: number]: string } = {
            1000: 'Normal closure',
            1001: 'Going away',
            1002: 'Protocol error',
            1003: 'Unsupported data',
            1005: 'No status received',
            1006: 'Abnormal closure',
            1007: 'Invalid frame payload data',
            1008: 'Policy violation',
            1009: 'Message too big',
            1010: 'Mandatory extension',
            1011: 'Internal server error',
            1015: 'TLS handshake failure'
        };
        return reasons[code] || `Unknown error (${code})`;
    }

    private attemptReconnect(): void {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            if (this.debug) {
                console.log('[SocketService] Max reconnect attempts reached');
            }

            // Emit final disconnect event when giving up
            this.disconnect$.next({
                code: 1006,
                reason: 'Max reconnect attempts reached',
                wasClean: false,
                timestamp: Date.now(),
                reconnectAttempt: this.reconnectAttempts
            });

            return;
        }

        const delay = this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts);
        if (this.debug) {
            console.log(`[SocketService] Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
        }

        this.reconnectTimer = setTimeout(() => {
            this.reconnectAttempts++;
            this.connect();
        }, delay);
    }

    /**
     * Manually trigger reconnection (useful for retry buttons or manual reconnection)
     */
    reconnect(): void {
        if (this.debug) {
            console.log('[SocketService] Manual reconnection triggered');
        }

        // Reset manual disconnect flag
        this.manualDisconnect = false;

        // Clear any existing reconnect timer
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        // Reset reconnect attempts for manual reconnection
        this.reconnectAttempts = 0;

        // Close existing connection if any
        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }

        // Start fresh connection
        this.connect();
    }

    sendMessage(action: string, data: any = {}): boolean {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            if (this.debug) {
                console.error(`[SocketService] Cannot send message with action ${action}: WebSocket not connected`);
            }
            return false;
        }

        const message = {
            action,
            ...data
        };

        if (this.debug) {
            console.log('[SocketService] Sending message:', message);
        }
        this.socket.send(JSON.stringify(message));
        return true;
    }

    send(data: any): boolean {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            if (this.debug) {
                console.error('[SocketService] Cannot send: WebSocket not connected');
            }
            return false;
        }

        const payload = typeof data === 'string' ? data : JSON.stringify(data);
        this.socket.send(payload);
        return true;
    }

    on<T = any>(type: string): Observable<T> {
        return this._events$.pipe(
            filter(event => event.type === type),
            map(event => event.data as T),
            shareReplay(1)
        );
    }

    isConnected(): Observable<boolean> {
        return this.connected$.asObservable();
    }

    getToken(): string | null {
        return this.token;
    }

    getErrors(): Observable<any> {
        return this.error$.asObservable();
    }

    onDisconnect(): Observable<DisconnectEvent> {
        return this.disconnect$.asObservable();
    }

    getProjectId(): string | null {
        return this.projectId;
    }

    getSessionId(): string | null {
        return this.sessionId;
    }

    disconnect(): void {
        // Mark as manual disconnect to prevent auto-reconnect
        this.manualDisconnect = true;

        // Clear any reconnect timers
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.socket) {
            // Use code 1000 to indicate normal closure
            this.socket.close(1000, 'User initiated disconnect');
            this.socket = null;
        }

        this.connected$.next(false);
    }

    destroy(): void {
        this.disconnect();
        this._destroy$.next();
        this._destroy$.complete();
        this.connected$.complete();
        this.error$.complete();
        this._events$.complete();
        this.disconnect$.complete();
    }
}

export const socketService = new SocketService();